import { VerifiedBadge } from "../components/VerifiedBadge";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { Avatar, PostComposer, PostList } from "../components/Posts";
import { ShareButton } from "../components/ShareButton";
import { FollowersButton } from "../components/FollowersButton";
import { formatProName } from "../lib/proName";
import { useSocialMeta } from "../lib/socialMeta";
import { InPersonBooking } from "../components/InPersonBooking";
import { PublicBio } from "../components/PublicBio";

const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function ProfessionalDetail({ idOverride } = {}) {
  const routeParams = useParams();
  const id = idOverride || routeParams.id;
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [pro, setPro] = useState(null);
  const [slots, setSlots] = useState(null); // null = non caricati (lazy)
  const [ipAvail, setIpAvail] = useState(null); // agenda in presenza del pro (lazy)
  const [bookMode, setBookMode] = useState("video"); // sotto-selettore area prenotazione
  const modeInit = useRef(false);
  const [selDay, setSelDay] = useState(null);
  const [booking, setBooking] = useState(false);
  const [instantBusy, setInstantBusy] = useState(false);
  const [postsKey, setPostsKey] = useState(0);
  const [params, setParams] = useSearchParams();
  const bookRef = useRef(null);
  const load = () => api.get(`/professionals/${id}`).then(({data}) => setPro(data));
  useEffect(() => { load(); }, [id]);
  // Titolo scheda browser (document.title) con appellativo nella lingua attiva; ripristino all'uscita
  useEffect(() => {
    if (!pro) return;
    document.title = `${formatProName({ title: pro.profile.professional_title, first_name: pro.user.first_name, last_name: pro.user.last_name }, i18n.language)} — YouPAM`;
    return () => { document.title = "YouPAM"; };
  }, [pro, i18n.language]);

  // Meta social dinamici (OG/Twitter) nell'<head> della SPA per la route pubblica reale.
  useSocialMeta(pro ? {
    title: formatProName({ title: pro.profile.professional_title, first_name: pro.user.first_name, last_name: pro.user.last_name }, i18n.language),
    description: [pro.profile.profession, pro.profile.specialization].filter(Boolean).join(" · ") || "Professionista sanitario su YouPAM",
    image: pro.avatar_url,
    url: `${window.location.origin}/professionals/${id}`,
    type: "profile",
  } : null);

  // Azione pendente da profilo pubblico anonimo: eseguita UNA sola volta dopo l'auth e SOLO per pazienti.
  useEffect(() => {
    if (!u || !pro) return;
    const raw = localStorage.getItem("pending_action");
    if (!raw) return;
    let pa; try { pa = JSON.parse(raw); } catch { localStorage.removeItem("pending_action"); return; }
    if (pa.kind !== "pro" || pa.id !== id) return;
    localStorage.removeItem("pending_action");
    if (u.role !== "user") { toast.info(t("actionx.patient_only")); return; }
    if (pa.type === "follow") { if (!pro.is_following) api.post(`/professionals/${id}/follow`).then(load).catch(() => {}); }
    else if (pa.type === "chat") nav(`/chat/${id}`);
  }, [u, pro, id]);

  const isOwn = u?.id === id && u?.role === "professional";
  // Scheda: proprietario solo Post; visitatore Post (default) o Prenota (?tab=book mantiene lo stato su reload/ritorno checkout)
  const tab = isOwn ? "posts" : (params.get("tab") === "book" ? "book" : "posts");
  const setTab = (k) => setParams(k === "book" ? { tab: "book" } : {}, { replace: true });

  useEffect(() => {
    if (tab === "book" && slots === null && !isOwn)
      api.get(`/professionals/${id}/slots`).then(({data}) => setSlots(data)).catch(() => setSlots([]));
  }, [tab, id, slots, isOwn]);
  // Agenda in presenza caricata subito (serve alle CTA in testata per mostrare "Prenota in presenza")
  useEffect(() => {
    if (!isOwn) api.get(`/professionals/${id}/in-person-availability`).then(({data}) => setIpAvail(data)).catch(() => setIpAvail({ weekly: [] }));
  }, [id, isOwn]);
  // Default sotto-modalità prenotazione: se il pro non ha videoconsulto ma ha l'in presenza, parti da "In presenza" (una sola volta)
  useEffect(() => {
    if (modeInit.current || ipAvail === null || !pro) return;
    modeInit.current = true;
    const pc = pro.profile?.price_cents;
    const vv = typeof pc === "number" && pc > 0;
    const ip = (ipAvail?.weekly?.length || 0) > 0 && (ipAvail?.price_cents || 0) > 0;
    if (!vv && ip) setBookMode("inperson");
  }, [ipAvail, pro]);

  const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Operazione non riuscita. Riprova."; };
  const follow = async () => {
    try {
      if (pro?.is_following) { await api.post(`/professionals/${id}/unfollow`); toast.success("Non segui più"); }
      else { await api.post(`/professionals/${id}/follow`); toast.success("Ora segui"); }
      load();
    } catch(e) { toast.error(errMsg(e)); }
  };
  const book = async (slot_start) => {
    // Visitatore non autenticato: reindirizza in modo pulito al login (niente chiamata API né toast tecnico auth_required).
    if (!u) { nav(`/login?next=${encodeURIComponent(`/professionals/${id}?tab=book`)}`); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/bookings/create-checkout", {
        professional_id: id, slot_start, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch(e) { toast.error(errMsg(e)); }
    finally { setBooking(false); }
  };
  const prenotaOra = async () => {
    // Visitatore non autenticato: apri il flusso di accesso/registrazione e torna a questo profilo su ?tab=book.
    // Nessuna chat/richiesta/pagamento prima dell'autenticazione.
    if (!u) { nav(`/login?next=${encodeURIComponent(`/professionals/${id}?tab=book`)}`); return; }
    setInstantBusy(true);
    try {
      const { data } = await api.post("/instant/request", { professional_id: id });
      nav(`/chat/${id}`, { state: { prefill: data.prefill } });
    } catch(e) { toast.error(errMsg(e)); }
    finally { setInstantBusy(false); }
  };
  const requireAuthAction = (action) => {
    // Nessun follow/chat prima dell'auth: memorizza l'azione e torna a questo profilo (solo path relativo).
    localStorage.setItem("pending_action", JSON.stringify({ type: action, kind: "pro", id }));
    nav(`/login?next=${encodeURIComponent(`/professionals/${id}`)}`);
  };
  const onFollow = () => { if (!u) return requireAuthAction("follow"); follow(); };
  const onChat = () => { if (!u) return requireAuthAction("chat"); nav(`/chat/${id}`); };
  const openBooking = (mode = "video") => { modeInit.current = true; setBookMode(mode); setTab("book"); setTimeout(() => bookRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); };

  // Raggruppamento slot per giorno: solo futuri, un giorno alla volta
  const days = useMemo(() => {
    if (!slots) return [];
    const m = new Map();
    const now = Date.now();
    for (const s of slots) {
      if (new Date(s.start_at).getTime() <= now) continue; // mai slot passati
      const k = s.local_date || dayKey(s.start_at);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [slots]);
  useEffect(() => { if (days.length && !days.some(([k]) => k === selDay)) setSelDay(days[0][0]); }, [days, selDay]);

  if (!pro) return <Layout><div className="yp-container py-8">{t("common.loading")}</div></Layout>;
  const priceCents = pro.profile.price_cents;
  const priceValid = typeof priceCents === "number" && Number.isFinite(priceCents) && priceCents > 0;
  const hasVideo = priceValid;
  const hasInPerson = (ipAvail?.weekly?.length || 0) > 0 && (ipAvail?.price_cents || 0) > 0;
  const selSlots = days.find(([k]) => k === selDay)?.[1] || [];

  return (<Layout><div className="yp-container max-w-4xl py-8">
    <div className="yp-card p-6 yp-gradient-border">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Avatar url={pro.avatar_url} label={pro.user.first_name} cls="w-16 h-16 text-xl" />
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">{formatProName({ title: pro.profile.professional_title, first_name: pro.user.first_name, last_name: pro.user.last_name }, i18n.language)}{pro.user.verified && <VerifiedBadge size={22} />}</h1>
            <div className="text-slate-500">{pro.profile.profession} · {pro.profile.specialization}</div>
            <div className="text-sm text-slate-500 mt-1">Albo: {pro.profile.board} #{pro.profile.board_number}</div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {!isOwn && <button className="yp-btn yp-btn-primary" onClick={onFollow} data-testid="follow-btn">{pro.is_following ? `✓ ${t("followx.following")}` : `+ ${t("followx.follow")}`}</button>}
        {!isOwn && <button className="yp-btn yp-btn-outline" onClick={onChat} data-testid="chat-btn">💬 {t("followx.chat")}</button>}
        {!isOwn && <button className="yp-btn yp-btn-primary" onClick={() => openBooking("video")} data-testid="book-tab-cta">📅 {t("pprof.tab_book")}</button>}
        {!isOwn && hasInPerson && <button className="yp-btn yp-btn-primary" onClick={() => openBooking("inperson")} data-testid="book-inperson-cta">📍 {t("pprof.book_inperson")}</button>}
        {isOwn && <FollowersButton />}
        {isOwn && <Link to="/agenda" className="yp-btn yp-btn-outline" data-testid="manage-agenda-btn">⚙️ {t("pprof.manage_agenda")}</Link>}
      </div>
      <div className="mt-4"><ShareButton url={pro.public_url} title={`${formatProName({ title: pro.profile.professional_title, first_name: pro.user.first_name, last_name: pro.user.last_name }, i18n.language)} — YouPAM`} /></div>
    </div>

    <PublicBio title={t("orgx.about_me")} text={pro.profile.public_bio || pro.profile.bio} testid="pro-about" />

    {!isOwn && (
      <div className="mt-6 flex gap-2 border-b border-slate-200" role="tablist" aria-label={t("pprof.tabs_label")} data-testid="pro-tabs">
        <button role="tab" aria-selected={tab === "posts"} onClick={() => setTab("posts")} data-testid="tab-posts"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "posts" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t("pprof.tab_posts")}</button>
        <button role="tab" aria-selected={tab === "book"} onClick={() => setTab("book")} data-testid="tab-book"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "book" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t("pprof.tab_book_label")}</button>
      </div>)}

    {!isOwn && tab === "book" && (
      <div className="mt-6" ref={bookRef} data-testid="booking-section">
        {hasInPerson && (
          <div className="flex gap-2 mb-4 border-b border-slate-200" role="tablist" aria-label={t("pprof.tab_book")} data-testid="book-mode-tabs">
            <button role="tab" aria-selected={bookMode === "video"} onClick={() => setBookMode("video")} data-testid="book-mode-video"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${bookMode === "video" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t("inperson.tab_video")}</button>
            <button role="tab" aria-selected={bookMode === "inperson"} onClick={() => setBookMode("inperson")} data-testid="book-mode-inperson"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${bookMode === "inperson" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t("inperson.tab_inperson")}</button>
          </div>)}
        {bookMode === "video" && (<div data-testid="book-video-section">
          {(!u || u.role === "user") && priceValid && (
            <div className="yp-card p-4 mb-4 border-blue-200 bg-blue-50/50" data-testid="instant-book-card">
              <h3 className="font-semibold text-slate-800">{t("instant.today_title")}</h3>
              <p className="text-sm text-slate-600 mt-1">{t("instant.today_info")}</p>
              <button className="yp-btn yp-btn-primary mt-3" onClick={prenotaOra} disabled={instantBusy} data-testid="prenota-ora-btn">{t("instant.book_now")}</button>
            </div>)}
          <h2 className="text-xl font-bold mb-3" data-testid="slots-title">{t("pprof.tab_book")}{priceValid && ` (€ ${(priceCents/100).toFixed(2)})`}</h2>
          <p className="text-xs text-slate-500 mb-3">{t("consult.cancel_48")}</p>
          {!priceValid ? (
            <p className="text-slate-500" data-testid="price-missing-visitor">{t("consult.slots_unavailable")}</p>
          ) : slots === null ? (
            <div className="text-slate-400">{t("common.loading")}</div>
          ) : days.length === 0 ? (
            <p className="text-slate-500" data-testid="no-days">{t("consult.slots_unavailable")}</p>
          ) : (<>
            <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label={t("pprof.choose_day")} data-testid="slot-days">
              {days.map(([k]) => (
                <button key={k} role="tab" aria-selected={selDay === k} onClick={() => setSelDay(k)} data-testid="slot-day"
                  className={`yp-btn !py-1.5 !px-3 text-sm shrink-0 ${selDay === k ? "yp-btn-primary" : "yp-btn-outline"}`}>
                  {new Date(`${k}T12:00:00`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}
                </button>))}
            </div>
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" aria-label={t("pprof.times")} data-testid="slot-times">
              {selSlots.map((s, i) => (
                <button key={i} className="yp-card p-2 text-sm text-center hover:border-blue-400 transition-colors" onClick={()=>book(s.start_at)} disabled={booking} data-testid="book-slot-btn">
                  {s.local_time || new Date(s.start_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
                </button>))}
            </div>
          </>)}
        </div>)}
        {bookMode === "inperson" && hasInPerson && (
          <div data-testid="book-inperson-section">
            <InPersonBooking proId={id} proName={formatProName({ title: pro.profile.professional_title, first_name: pro.user.first_name, last_name: pro.user.last_name }, i18n.language)} />
          </div>)}
      </div>)}

    {(isOwn || tab === "posts") && (
      <div className="mt-6" data-testid="pro-posts-section">
        {isOwn && <PostComposer onPosted={() => setPostsKey(k => k + 1)} />}
        <PostList key={postsKey} authorId={id} canManage={isOwn} />
      </div>)}
  </div></Layout>);
}
